// Fix Format: the only action that rewrites course names on disk. Narrow by design.
//
// GOVERNING PRINCIPLE - NEVER GUESS. Every resolver returns null when it cannot
// determine the correct value with certainty. A null is reported for manual fixing; it
// is never written. A wrong automated rewrite of a course name is far more damaging
// than an unfixed row, and the user has no undo.
//
// Scope:
//   every segment - blank space next to a tilde is trimmed (FF-0). This is the one change
//       made outside segments 4, 5 and 8, and it is safe to automate because removing
//       surrounding space cannot change what a segment says.
//   4 - module name (protected spellings, and the mapping's exact name when it is
//       unambiguously the same module)
//   5 - cohort format
//   8 - session format
// Apart from that trimming, every other segment is left byte for byte untouched.
// A cohort that disagrees with its category heading (CD-9) is never rewritten: nothing
// says whether the heading or the course is the wrong one.

import {
  applyProtectedWordFixes,
  isValidCohort,
  isValidSession,
  MONTH_TO_CODE,
  nameKey,
  parseValue,
  segmentCount,
  splitSegments,
} from "./courseNameRules";
import { collectCourseRows } from "./courseRows";
import { getModuleIndex, isSameModuleName } from "./moduleMappings";

/**
 * Work out the corrected form of one course name.
 *
 * Splits WITHOUT dropping trailing empties, trims every piece, replaces only indices 3, 4
 * and 7, and rejoins. That keeps the trailing tilde while removing the space around it.
 *
 * @returns {{value, changed, changes: Array, unfixed: Array, incomplete: Array}|null}
 *          null when the row is not a well-formed 8-segment name
 */
export const fixCourseName = (raw, index = getModuleIndex()) => {
  // The same gate CD-1 uses. Without it a name with extra internal tildes would have
  // 8+ pieces and we would positionally rewrite indices 3/4/7 of a row Check Doc has
  // already called structurally broken, writing garbage into it.
  if (segmentCount(raw) !== 8) return null;

  const parts = splitSegments(raw);
  const changes = [];
  const unfixed = [];
  const incomplete = [];

  // --- FF-0 blank space next to a tilde, in every piece ----------------------------
  // Runs first so the rules below see the same trimmed values Check Doc validates.
  // Pieces past the eighth are the trailing tilde's tail; the gate above guarantees they
  // are blank, so trimming leaves the tilde itself in place.
  parts.forEach((piece, i) => {
    const trimmed = piece.trim();
    if (trimmed === piece) return;
    changes.push({
      segment: i < 8 ? i + 1 : null,
      before: piece,
      after: trimmed,
      label: i < 8 ? `segment ${i + 1} spaces` : "spaces after the last ~",
    });
    parts[i] = trimmed;
  });

  // --- FF-1 segment 4 -------------------------------------------------------------
  const originalName = parts[3];
  let name = originalName;

  // FF-1b: the mapping's exact name, but only when this is unmistakably the same
  // module written wrong. If segment 1's code were itself mistyped it would point at a
  // different module and the distance would be far outside the limit.
  const code = String(parts[0]).toUpperCase();
  const knownModule = index.modules[code];
  const knownName = knownModule && knownModule.name;
  if (knownName && !index.nameKeyToCode[nameKey(name)]) {
    const verdict = isSameModuleName(name, knownName);
    if (verdict && verdict.same && knownName !== name) {
      name = knownName;
    }
  }

  // FF-1a: protected spellings, applied to whatever segment 4 ended up as.
  name = applyProtectedWordFixes(name);

  if (name !== originalName) {
    changes.push({ segment: 4, before: originalName, after: name, label: "module name" });
    parts[3] = name;
  }

  // --- FF-2 segment 5, cohort -----------------------------------------------------
  const originalCohort = parts[4];
  if (!isValidCohort(originalCohort)) {
    const parsed = parseValue(originalCohort);
    const fixed = parsed ? `${parsed.month}${parsed.year}` : null;

    if (fixed && fixed !== originalCohort) {
      changes.push({ segment: 5, before: originalCohort, after: fixed, label: "cohort" });
      parts[4] = fixed;
    } else if (!fixed) {
      unfixed.push({
        segment: 5,
        value: originalCohort,
        reason: `cohort "${originalCohort}" could not be resolved to a month + year`,
      });
    }
  }

  // --- FF-3 segment 8, session ----------------------------------------------------
  const originalSession = parts[7];
  if (!isValidSession(originalSession)) {
    const parsed = parseValue(originalSession);
    const sessionCode = parsed ? MONTH_TO_CODE[parsed.month.toLowerCase()] : null;
    const fixed = sessionCode ? `${sessionCode}${parsed.year}${parsed.group}` : null;

    if (fixed && fixed !== originalSession) {
      changes.push({ segment: 8, before: originalSession, after: fixed, label: "session" });
      parts[7] = fixed;

      // The group letter is never invented, so a source with none rebuilds to something
      // that is still not a valid session. Writing it is still an improvement, but say
      // so plainly or Check Doc will re-flag the row every run with no explanation.
      if (!isValidSession(fixed)) {
        incomplete.push({
          segment: 8,
          value: fixed,
          reason: `session "${originalSession}" became "${fixed}" but still needs a group letter`,
        });
      }
    } else if (!fixed) {
      unfixed.push({
        segment: 8,
        value: originalSession,
        reason:
          parsed && !sessionCode
            ? `session "${originalSession}" is in ${parsed.month}, which has no session code`
            : `session "${originalSession}" could not be resolved to a session letter + year`,
      });
    }
  }

  const value = parts.join("~");
  return { value, changed: value !== raw, changes, unfixed, incomplete };
};

/**
 * Plan the fixes for the whole sheet. Performs no I/O: it returns the edit list, which
 * keeps a dry run possible and keeps the write in one place.
 *
 * @param {Array<Array>} rows - sheet as an array of row arrays
 * @param {{startRow: number, index: object}} options
 */
export const planCourseNameFixes = (rows, options = {}) => {
  const index = options.index || getModuleIndex();
  const { courseRows, scanned } = collectCourseRows(rows, options.startRow);

  const edits = [];
  const changed = [];
  const unresolved = [];
  const incomplete = [];
  const skipped = [];

  courseRows.forEach((entry) => {
    const result = fixCourseName(entry.raw, index);

    if (!result) {
      skipped.push({
        rowNumber: entry.rowNumber,
        ref: entry.ref,
        reason: `has ${segmentCount(entry.raw)} tilde segments, not 8`,
      });
      return;
    }

    result.unfixed.forEach((item) =>
      unresolved.push({ rowNumber: entry.rowNumber, ref: entry.ref, ...item })
    );
    result.incomplete.forEach((item) =>
      incomplete.push({ rowNumber: entry.rowNumber, ref: entry.ref, ...item })
    );

    if (!result.changed) return;

    // Written back to the SAME cell the name was read from, B or C.
    edits.push({ ref: entry.ref, value: result.value });
    changed.push({
      rowNumber: entry.rowNumber,
      ref: entry.ref,
      before: entry.raw,
      after: result.value,
      changes: result.changes,
    });
  });

  return {
    edits,
    changed,
    unresolved,
    incomplete,
    skipped,
    scanned,
    courseRowCount: courseRows.length,
    rowsChanged: changed.length,
  };
};
